function changeHeightWidth(
  height: number,
  maxHeight: number,
  width: number,
  maxWidth: number,
  minWidth?: number,
  minHeight?: number,
) {
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }
  if (height > maxHeight) {
    width = Math.round((width * maxHeight) / height);
    height = maxHeight;
  }
  if (minWidth && width < minWidth) {
    height = Math.round((height * minWidth) / width);
    width = minWidth;
  }
  if (minHeight && height < minHeight) {
    width = Math.round((width * minHeight) / height);
    height = minHeight;
  }
  return { height, width };
}

function resizeAndRotateImage(
  image: HTMLImageElement,
  maxWidth: number,
  maxHeight: number,
  minWidth: undefined | number,
  minHeight: undefined | number,
  compressFormat = 'jpeg',
  quality = 100,
  rotation = 0,
) {
  const qualityDecimal = quality / 100;
  const canvas = document.createElement('canvas');

  let width = image.width;
  let height = image.height;

  const newHeightWidth = changeHeightWidth(
    height,
    maxHeight,
    width,
    maxWidth,
    minWidth,
    minHeight,
  );
  if (rotation && (rotation === 90 || rotation === 270)) {
    canvas.width = newHeightWidth.height;
    canvas.height = newHeightWidth.width;
  } else {
    canvas.width = newHeightWidth.width;
    canvas.height = newHeightWidth.height;
  }

  width = newHeightWidth.width;
  height = newHeightWidth.height;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return;
  }
  ctx.fillStyle = 'rgba(0, 0, 0, 0)';
  ctx.fillRect(0, 0, width, height);

  if (ctx.imageSmoothingEnabled && ctx.imageSmoothingQuality) {
    ctx.imageSmoothingQuality = 'high';
  }

  if (rotation) {
    ctx.rotate((rotation * Math.PI) / 180);
    if (rotation === 90) {
      ctx.translate(0, -canvas.width);
    } else if (rotation === 180) {
      ctx.translate(-canvas.width, -canvas.height);
    } else if (rotation === 270) {
      ctx.translate(-canvas.height, 0);
    } else if (rotation === 0 || rotation === 360) {
      ctx.translate(0, 0);
    }
  }
  ctx.drawImage(image, 0, 0, width, height);

  return canvas.toDataURL(`image/${compressFormat}`, qualityDecimal);
}

function b64toByteArrays(b64Data: string): Uint8Array[] {
  const sliceSize = 512;

  const byteCharacters = atob(
    b64Data.toString().replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, ''),
  );
  const byteArrays: Uint8Array[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }
  return byteArrays;
}

function b64toBlob(b64Data: string, contentType = 'image/jpeg') {
  const byteArrays = b64toByteArrays(b64Data);
  const normalized = byteArrays.map((arr) => new Uint8Array(arr.buffer));

  return new Blob(normalized as BlobPart[], { type: contentType });
}

function b64toFile(
  b64Data: string,
  fileName: string,
  contentType = 'image/jpeg',
) {
  const byteArrays = b64toByteArrays(b64Data);

  // Normalize to standard Uint8Array backed by ArrayBuffer
  const normalized = byteArrays.map((arr) => new Uint8Array(arr.buffer));

  // Create the File
  const file = new File(normalized as BlobPart[], fileName, {
    type: contentType,
    lastModified: Date.now(), // timestamp in ms
  });

  return file;
}

function createResizedImage(
  file: File,
  maxWidth: number,
  maxHeight: number,
  compressFormat: string,
  quality: number,
  rotation: number,
  responseUriFunc: (blob: string | File | Blob) => void,
  outputType = 'base64',
  minWidth?: number,
  minHeigh?: number,
  onError?: (error: Error) => void,
) {
  const fail = (error: Error) => {
    if (!onError) {
      throw error;
    }

    onError(error);
  };

  const reader = new FileReader();
  if (file) {
    if (file.type && !file.type.includes('image')) {
      throw Error('File Is NOT Image!');
    } else {
      reader.readAsDataURL(file);
      reader.onload = () => {
        const image = new Image();
        image.src = reader.result?.toString() || '';
        image.onerror = () => fail(Error('Image Could Not Be Decoded!'));
        image.onload = function () {
          const resizedDataUrl = resizeAndRotateImage(
            image,
            maxWidth,
            maxHeight,
            minWidth,
            minHeigh,
            compressFormat,
            quality,
            rotation,
          );

          if (!resizedDataUrl) {
            fail(Error('Image Could Not Be Resized!'));
            return;
          }

          const contentType = `image/${compressFormat}`;
          switch (outputType) {
            case 'blob': {
              const blob = b64toBlob(resizedDataUrl, contentType);
              responseUriFunc(blob);
              break;
            }
            case 'base64':
              responseUriFunc(resizedDataUrl);
              break;
            case 'file': {
              const fileName = file.name;
              const fileNameWithoutFormat = fileName
                .toString()
                .replace(/(png|jpeg|jpg|webp)$/i, '');
              const newFileName = fileNameWithoutFormat.concat(
                compressFormat.toString(),
              );
              const newFile = b64toFile(
                resizedDataUrl,
                newFileName,
                contentType,
              );
              responseUriFunc(newFile);

              break;
            }
            default:
              responseUriFunc(resizedDataUrl);
          }
        };
      };
      reader.onerror = () => fail(Error('File Could Not Be Read!'));
    }
  } else {
    throw Error('File Not Found!');
  }
}

/**
 * The one way the kit resizes a picked image before it travels. JPEG rather than WebP
 * because canvas export only has to support JPEG and PNG: where WebP is not supported the
 * canvas silently hands back an uncompressed PNG and ignores the quality asked for, which
 * on a phone camera shot is megabytes where this is hundreds of kilobytes.
 */
function resizeImageFile(
  file: File,
  {
    maxWidth = 2048,
    maxHeight = 2048,
    compressFormat = 'JPEG',
    quality = 80,
  }: {
    maxWidth?: number;
    maxHeight?: number;
    compressFormat?: string;
    quality?: number;
  } = {},
) {
  return new Promise<string>((resolve, reject) => {
    try {
      createResizedImage(
        file,
        maxWidth,
        maxHeight,
        compressFormat,
        quality,
        0,
        (uri) =>
          typeof uri === 'string'
            ? resolve(uri)
            : reject(Error('Resized Image Is Not A Data URL!')),
        'base64',
        undefined,
        undefined,
        reject,
      );
    } catch (error) {
      reject(error instanceof Error ? error : Error(String(error)));
    }
  });
}

export { createResizedImage, resizeImageFile };
